import React, { useState, useEffect, useCallback } from 'react';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { apiService, User, Product } from '../services/api';
import ConfirmModal from './ConfirmModal';

interface UserSubscriptionsProps {
  onBack: () => void;
}

const UserSubscriptions: React.FC<UserSubscriptionsProps> = ({ onBack }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingSubscriptions, setUpdatingSubscriptions] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubscription, setSelectedSubscription] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);

  // Modal states
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    userId: number | null;
    productId: number | null;
    message: string;
  }>({
    visible: false,
    userId: null,
    productId: null,
    message: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        subscriptionId: selectedSubscription === 'all' ? undefined : 
                       selectedSubscription === 'none' ? 'null' : selectedSubscription, // This filters by productId in backend
      };
      
      const [usersResponse, productsResponse] = await Promise.all([
        apiService.getUsers(params),
        apiService.getProducts({})
      ]);
      
      setUsers(usersResponse?.users ?? []);
      setTotalPages(usersResponse?.pagination?.totalPages ?? 1);
      setProducts(productsResponse?.products ?? []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, selectedSubscription]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm || selectedSubscription !== 'all' || searchTerm === '') {
        setCurrentPage(1); // Reset to first page when searching
        fetchData();
      }
    }, 300); // 300ms delay

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedSubscription, fetchData]);

  // Fetch data when page changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubscriptionChange = (userId: number, productId: number | undefined) => {
    // Get user and product information for confirmation
    const user = users.find(u => u.id === userId);
    const currentSubscription = getActiveSubscription(user!);
    const currentProductName = currentSubscription ? getSubscriptionName(user!) : 'No Subscription';
    
    if (productId) {
      const newProduct = products.find(p => p.id === productId);
      const newProductName = newProduct?.name || 'selected product';
      
      // Show confirmation dialog
      setConfirmModal({
        visible: true,
        userId,
        productId,
        message: `Are you sure you want to change subscription for "${user?.name || user?.email}" from "${currentProductName}" to "${newProductName}"?`,
      });
    } else {
      // Note: To remove subscription, we would need to cancel the active subscription
      // For now, we'll just show an error message
      alert('To remove subscription, please cancel it from the subscription management page.');
    }
  };

  const confirmSubscriptionChange = async () => {
    if (!confirmModal.userId || !confirmModal.productId) return;
    
    try {
      // Set loading state for this specific user
      setUpdatingSubscriptions(prev => new Set(prev).add(confirmModal.userId!));
      
      await apiService.updateUserSubscription(confirmModal.userId, confirmModal.productId);
      
      // Show success message
      console.log('Subscription updated successfully!');
      
      // Close modal
      setConfirmModal({ visible: false, userId: null, productId: null, message: '' });
      
      // Auto-reload data after a short delay
      setTimeout(() => {
        fetchData();
      }, 500);
      
    } catch (error) {
      console.error('Failed to update user subscription:', error);
      // Show error message to user
      alert('Failed to update subscription. Please try again.');
    } finally {
      // Clear loading state
      setUpdatingSubscriptions(prev => {
        const newSet = new Set(prev);
        newSet.delete(confirmModal.userId!);
        return newSet;
      });
    }
  };

  // No need for client-side filtering since we're doing server-side filtering
  const filteredUsers = users;

  const getActiveSubscription = (user: User) => {
    if (!user.subscriptions || user.subscriptions.length === 0) return null;
    // Get the most recent active subscription
    return user.subscriptions.find(sub => sub.status === 'ACTIVE') || user.subscriptions[0];
  };

  const getSubscriptionName = (user: User) => {
    const activeSub = getActiveSubscription(user);
    if (!activeSub) return 'No Subscription';
    return activeSub.productName || activeSub.product?.name || 'Unknown';
  };

  const getSubscriptionPrice = (user: User) => {
    const activeSub = getActiveSubscription(user);
    if (!activeSub) return '';
    const price = activeSub.currentPrice || activeSub.product?.basePrice || 0;
    return `IDR ${new Intl.NumberFormat('id-ID').format(price)}`;
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">User Subscriptions Management</h1>
        </div>
      </div>

      {/* Content */}
      {/* <div className="px-4 sm:px-6 lg:px-8 py-8"> */}
        {/* Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4 bg-white rounded-lg shadow p-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <div className="relative">
              <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select
                value={selectedSubscription}
                onChange={(e) => setSelectedSubscription(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-transparent appearance-none"
              >
                <option value="all">All Subscriptions</option>
                <option value="none">No Subscription</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id.toString()}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Subscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Change Subscription
                  </th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th> */}
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th> */}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {user.avatar ? (
                              <img
                                className="h-10 w-10 rounded-full"
                                src={user.avatar}
                                alt={user.name || user.email}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-700">
                                  {user.name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 text-left">
                              {user.name || 'No Name'}
                            </div>
                            <div className="text-sm text-gray-500 text-left">{user.email}</div>
                            {user.phone && (
                              <div className="text-sm text-gray-500 text-left">{user.phone}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        {getActiveSubscription(user) ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900 text-left">
                              {getSubscriptionName(user)}
                            </div>
                            <div className="text-sm text-gray-500 text-left">
                              {getSubscriptionPrice(user)}
                            </div>
                            <div className="text-xs text-gray-400 mt-1 text-left">
                              Status: {getActiveSubscription(user)?.status}
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            No Subscription
                          </span>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <select
                          value={getActiveSubscription(user)?.productId || ''}
                          onChange={(e) => handleSubscriptionChange(user.id, e.target.value ? parseInt(e.target.value) : undefined)}
                          disabled={updatingSubscriptions.has(user.id)}
                          className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-imola-primary focus:border-transparent text-sm ${
                            updatingSubscriptions.has(user.id) ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <option value="">
                            {updatingSubscriptions.has(user.id) ? 'Updating...' : 'No Subscription'}
                          </option>
                          {products.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name} - IDR {new Intl.NumberFormat('id-ID').format(product.basePrice)}
                            </option>
                          ))}
                        </select>
                      </td>
                      
                      {/* <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td> */}
                      
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewUser(user)}
                            className="text-indigo-600 hover:text-indigo-900 p-1"
                            title="View User"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Edit User"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td> */}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, totalPages * itemsPerPage)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{totalPages * itemsPerPage}</span>
                  {' '}results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  {Array.from(Array(Math.min(5, totalPages)), (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-imola-primary border-imola-primary text-white'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
        </div>

        {/* Pagination */}
        
      {/* </div> */}

      {/* Confirm Modal */}
      <ConfirmModal
        visible={confirmModal.visible}
        title="Confirm Subscription Change"
        message={confirmModal.message}
        confirmText="Change Subscription"
        cancelText="Cancel"
        onConfirm={confirmSubscriptionChange}
        onCancel={() => setConfirmModal({ visible: false, userId: null, productId: null, message: '' })}
      />
    </div>
  );
};

export default UserSubscriptions;
